import { createContext } from 'preact'
import { useContext } from 'preact/hooks'

export type RoomTheme = 'light' | 'dark'

/** Also read by the page's head (WebinarRoomPageBuilder.ThemeStorageKey), which marks <html> dark before it paints. */
export const ROOM_THEME_STORAGE_KEY = 'ox-room:theme'

/**
 * The theme this browser last chose for the room, kept in localStorage. Light unless it chose dark: the client designs
 * the room for light, so the system's own preference is never asked. Storage that refuses to be read leaves it light.
 */
export function storedRoomTheme(storage: Pick<Storage, 'getItem'> | null): RoomTheme {
    try {
        return storage?.getItem(ROOM_THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light'
    } catch {
        return 'light'
    }
}

export function storeRoomTheme(storage: Pick<Storage, 'setItem'> | null, theme: RoomTheme): void {
    try {
        storage?.setItem(ROOM_THEME_STORAGE_KEY, theme)
    } catch {
        /* Refused storage: the choice lasts as long as the page. */
    }
}

/**
 * The viewer page's theme, and how to switch it. Previews, thumbnails and the moderator room have no provider, so they
 * draw light, as viewers first see the room, and offer no switch.
 */
export interface RoomThemeState {
    theme: RoomTheme
    toggle: (() => void) | null
}

export const RoomThemeContext = createContext<RoomThemeState>({ theme: 'light', toggle: null })

export const useRoomTheme = () => useContext(RoomThemeContext)
